import { useCallback } from 'react';
import { InfiniteData, useQueryClient } from 'react-query';

import { useAgGridClientTableContext } from 'core/states';
import { GetManyResponse } from 'core/types';
import { QuoteVersionType } from 'quotes/enums';
import { quotePartService } from 'quotes/services';
import { ICustomerQuotePart } from 'quotes/types';

/**
 * Re-reads the lines a save changed besides the ones it returns (affectedQuotePartIds) and updates
 * them in the grid and in the cached list. A refetch would not: the grid takes a refetched row only
 * once its updatedAt moves, and a Valid until shared across a vendor quote's lines leaves it as it
 * was, so a grid seeded again from a stale cache would keep the old dates.
 *
 * The list endpoint matches a missing version as none, so the caller passes the grid's own.
 */
export const useRefreshQuotePartRows = (
  quoteId?: number | null,
  quoteVersion?: QuoteVersionType
) => {
  const queryClient = useQueryClient();
  const { gridApi, updateRows } = useAgGridClientTableContext();

  return useCallback(
    async (quotePartIds?: number[] | null): Promise<void> => {
      const ids = (quotePartIds ?? []).filter((id) => !!gridApi?.getRowNode(String(id)));

      if (!quoteId || !quoteVersion || !ids.length) {
        return;
      }

      try {
        const filters = { 'quoteVersion.type': quoteVersion };
        const { records } = await quotePartService.getCustomerQuoteParts({
          quoteId,
          filters: { ids, ...filters },
          page: 1,
          pageSize: ids.length,
        });

        updateRows({ update: records }, { refreshCells: true, redrawRows: true });

        const recordsById = new Map(records.map((record) => [record.id, record]));

        queryClient.setQueriesData<InfiniteData<GetManyResponse<ICustomerQuotePart>> | undefined>(
          ['customerQuoteParts', { quoteId, filters }],
          (data) =>
            data && {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                records: page.records.map((record) => recordsById.get(record.id) ?? record),
              })),
            }
        );
      } catch (error) {
        // The save itself went through; the other lines show their new dates on the next load.
        console.error(error);
      }
    },
    [gridApi, queryClient, quoteId, quoteVersion, updateRows]
  );
};
